import React, { FC, useMemo, useState } from 'react';
import { ReactComponent as CancelTripIcon } from 'assets/icons/cancel-trip-modal-icon.svg';
import { toast } from 'react-toastify';
import {
  Button,
  Input,
  Textarea,
  ToastMessage,
  BaseModal,
  FileUploader
} from '@cokitchen/cokitchen-components';
import { OrderType } from 'models/order.model';
import { apiService } from 'utils/api';
import { handleApiError } from 'utils/functions';
import { STORAGE_KEYS } from 'utils/constants';

const CANCEL_PREPAID_ORDER_REASONS = [
  'Delayed preparation',
  'Delayed Delivery',
  'Sold Out Items',
  'Wrong Delivery Address',
  'Payment Failure',
  'Order Modification',
  'Duplicate Order',
  'Re-order Later',
  'Customer not responsive',
  'Other (Customer asked to cancel)'
];

interface CancelPrepaidOrderModalProps {
  show: boolean;
  onClose: () => void;
  order: OrderType;
}

const CancelPrepaidOrderModal: FC<CancelPrepaidOrderModalProps> = ({ show, onClose, order }) => {
  const orderCode = order?.order_code?.toUpperCase() ?? '';

  const [loading, setLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);

  const isFormValid = useMemo(() => !!selectedReason, [selectedReason]);

  const resetForm = () => {
    setSelectedReason('');
    setAdditionalNotes('');
    setImageUrls([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const payload = {
      order_code: order.order_code,
      cancelled_note: selectedReason,
      additional_notes: additionalNotes,
      image: imageUrls
    };

    try {
      setLoading(true);
      await apiService.post('orders/cancel', {
        data: payload,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage type='success' message='Order cancelled successfully' />);
      handleClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={show}
      position='right'
      title={`Cancel order #${orderCode}`}
      size='wide'
      className='w-[70vw] max-w-[unset]'
      description='Select a reason and provide any supporting details below.'
      titleIcon={<CancelTripIcon />}
      titleIconColor='danger'
      onRequestClose={handleClose}
      footer={
        <div className='grid grid-cols-2 gap-x-4'>
          <Button small color='primary' variant='outline' className='mx-auto' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            small
            color='primary'
            variant='block'
            className='mx-auto'
            loading={loading}
            disabled={!isFormValid}
            onClick={handleSubmit}>
            Confirm
          </Button>
        </div>
      }>
      <section>
        <div className='grid gap-6 grid-cols-2 mt-2'>
          {CANCEL_PREPAID_ORDER_REASONS.map((reason, i) => (
            <Input
              key={i}
              value={selectedReason}
              label={reason}
              type='radio'
              onChange={() => setSelectedReason(reason)}
              name='cancel-order-reason'
            />
          ))}
        </div>
      </section>

      <section className='mt-6'>
        <Textarea
          label='Additional notes (optional)'
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder='Add notes'
          className='w-full'
        />
      </section>

      <section className='mt-6'>
        <label className='font-medium'>Attach images (optional)</label>
        <FileUploader
          token={localStorage.getItem(STORAGE_KEYS.TOKEN) as string}
          fileUrls={imageUrls}
          setFileUrls={setImageUrls}
        />
      </section>
    </BaseModal>
  );
};

export default CancelPrepaidOrderModal;
